import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import * as TWEEN from '@tweenjs/tween.js';

import { createScene } from './components/Scene';
import { createLights } from './components/Lights';
import { createCafe } from './components/Cafe';
import { createCharacter, CharacterControllerBase } from './components/Character';
import { InputController } from './utils/InputController';
import { CollisionSystem } from './utils/CollisionSystem';
import { LoadingManager } from './utils/LoadingManager';
import { FPSCounter } from './utils/FPSCounter';
import { MultiplayerManager } from './MultiplayerManager';
import { WS_CLIENT_URL } from './config/env';
import { logger, LogLevel } from './utils/Logger';
import { musicPlayer } from './utils/MusicPlayer';
import { ThirdPartyGameHelper } from './utils/ThirdPartyGameHelper';
import { InventoryUI } from './components/InventoryUI';
import { VendingMachineUI } from './components/VendingMachineUI';

// Set logging level based on environment
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
logger.setLevel(isDevelopment ? LogLevel.ERROR : LogLevel.NONE);

// Canvas
const canvas = document.getElementById('canvas') as HTMLCanvasElement;

// Loading screen elements
const loadingScreen = document.getElementById('loading-screen') as HTMLDivElement;
// Hide loading screen initially
loadingScreen.style.display = 'none';

// Create start screen
const startScreen = document.createElement('div');
startScreen.style.position = 'absolute';
startScreen.style.top = '0';
startScreen.style.left = '0';
startScreen.style.width = '100%';
startScreen.style.height = '100%';
startScreen.style.backgroundColor = 'rgba(0, 0, 0, 0.6)'; // Make it semi-transparent
startScreen.style.display = 'flex';
startScreen.style.flexDirection = 'column';
startScreen.style.alignItems = 'center';
startScreen.style.justifyContent = 'center';
startScreen.style.zIndex = '1000';

// Create title with text shadow for better visibility
const title = document.createElement('h1');
title.textContent = 'Welcome to AI Cafe';
title.style.color = '#ffffff';
title.style.marginBottom = '20px';
title.style.fontFamily = 'Arial, sans-serif';
title.style.fontSize = '48px';
title.style.textShadow = '2px 2px 4px rgba(0, 0, 0, 0.5)';
title.style.textAlign = 'center'; // Ensure centered text
title.style.width = '100%'; // Full width for better centering
startScreen.appendChild(title);

// Add control instructions
const instructions = document.createElement('div');
// Check if user is on mobile device to show appropriate controls
const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

if (isMobileDevice) {
  instructions.textContent = 'Left joystick to move • Right joystick to look • E button to interact';
} else {
  instructions.textContent = 'WASD to move • Mouse to look around • Space to jump • E to interact • D to stop dancing';
}

instructions.style.color = '#ffffff';
instructions.style.fontSize = '20px';
instructions.style.marginBottom = '30px';
instructions.style.fontFamily = 'Arial, sans-serif';
instructions.style.textAlign = 'center';
instructions.style.maxWidth = '90%'; // Slightly larger to ensure all text fits on mobile
instructions.style.lineHeight = '1.5';
instructions.style.textShadow = '1px 1px 2px rgba(0, 0, 0, 0.5)';
startScreen.appendChild(instructions);

// Create name input with styling
const nameInput = document.createElement('input');
nameInput.type = 'text';
nameInput.placeholder = 'Enter your name';
nameInput.maxLength = 20;
nameInput.style.padding = '15px 25px';
nameInput.style.fontSize = '20px';
nameInput.style.marginBottom = '30px';
nameInput.style.borderRadius = '8px';
nameInput.style.border = '2px solid #8A2BE2';
nameInput.style.width = '250px';
nameInput.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
nameInput.style.outline = 'none';
startScreen.appendChild(nameInput);

// Create start button with enhanced styling
const startButton = document.createElement('button');
startButton.textContent = 'Enter Cafe';
startButton.style.padding = '15px 40px';
startButton.style.fontSize = '24px';
startButton.style.backgroundColor = '#8A2BE2';
startButton.style.color = 'white';
startButton.style.border = 'none';
startButton.style.borderRadius = '8px';
startButton.style.cursor = 'pointer';
startButton.style.transition = 'all 0.3s ease';
startButton.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
startButton.onmouseover = () => {
    startButton.style.backgroundColor = '#7B27CC';
    startButton.style.transform = 'translateY(-2px)';
    startButton.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.2)';
};
startButton.onmouseout = () => {
    startButton.style.backgroundColor = '#8A2BE2';
    startButton.style.transform = 'translateY(0)';
    startButton.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
};
startScreen.appendChild(startButton);

document.body.appendChild(startScreen);

// Scene setup
const scene = createScene();
const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
camera.position.set(0, 5, 10);

// Renderer setup
const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
});
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

// Loading manager
const loadingManager = new LoadingManager(loadingScreen, () => {
  // Loading is complete
  // Hide loading screen
  loadingScreen.style.opacity = '0';
  setTimeout(() => {
    loadingScreen.style.display = 'none';
  }, 500);
});

// Systems
const inputController = new InputController();
const collisionSystem = new CollisionSystem();
const fpsCounter = new FPSCounter();

// Make inputController globally available for chat and other components
(window as any).inputController = inputController;

// Game state
let character: THREE.Group | null = null;
let characterController: CharacterControllerBase | null = null;
let cafeModel: THREE.Group | null = null;
let multiplayerManager: MultiplayerManager | null = null;
let lastDebugTime = 0;

// Initialize interactive objects array
const interactiveObjects: { mesh: THREE.Object3D, url: string, label: string, interactionDistance: number }[] = [];

// Add to Window interface for global access
declare global {
  interface Window {
    interactiveObjects: typeof interactiveObjects;
    handleInteraction: typeof handleInteraction;
    registerInteractiveObject: typeof registerInteractiveObject;
    checkInteractiveObjects: typeof checkInteractiveObjects;
    character?: {
      controller: CharacterControllerBase;
      position?: THREE.Vector3;
    };
    multiplayerManager?: MultiplayerManager;
    inputController?: InputController;
    playerName?: string;
    portalInfo?: {
      fromPortal: boolean;
      referrer: string | null;
      username: string;
      color: string;
      speed: number;
    };
  }
}

window.interactiveObjects = interactiveObjects;
window.handleInteraction = handleInteraction;
window.registerInteractiveObject = registerInteractiveObject;
window.checkInteractiveObjects = checkInteractiveObjects;

// Create prompt element for interaction
const promptElement = document.createElement('div');
promptElement.style.position = 'absolute';
promptElement.style.bottom = '20%';
promptElement.style.left = '50%';
promptElement.style.transform = 'translateX(-50%)';
promptElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
promptElement.style.color = 'white';
promptElement.style.padding = '10px 20px';
promptElement.style.borderRadius = '5px';
promptElement.style.fontFamily = 'Arial, sans-serif';
promptElement.style.fontSize = '18px';
promptElement.style.display = 'none';
promptElement.style.zIndex = '1000';
document.body.appendChild(promptElement);

// Create mobile interaction button if needed - REMOVED in favor of the E button from MobileUI.ts
// const mobileInteractButton = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
//   ? (() => {
//     const button = document.createElement('button');
//     button.textContent = 'E';
//     button.style.position = 'absolute';
//     button.style.bottom = '30px';
//     button.style.right = '30px';
//     button.style.width = '60px';
//     button.style.height = '60px';
//     button.style.borderRadius = '50%';
//     button.style.backgroundColor = '#8A2BE2';
//     button.style.color = 'white';
//     button.style.border = 'none';
//     button.style.fontSize = '24px';
//     button.style.cursor = 'pointer';
//     button.style.zIndex = '1000';
//     button.style.display = 'none'; // Hide initially
//     document.body.appendChild(button);
//     button.addEventListener('click', handleInteraction);
//     return button;
//   })()
//   : null;
const mobileInteractButton = null;

// Function to check for nearby interactive objects
function checkInteractiveObjects(playerPosition: THREE.Vector3) {
  let closestObject = null;
  let closestDistance = Infinity;

  for (const obj of interactiveObjects) {
    const worldPos = new THREE.Vector3();
    obj.mesh.getWorldPosition(worldPos);
    const distance = playerPosition.distanceTo(worldPos);

    if (distance < obj.interactionDistance && distance < closestDistance) {
      closestObject = obj;
      closestDistance = distance;
    }
  }

  if (closestObject) {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const actionText = isMobile ? 'Tap E' : 'Press E';
    promptElement.textContent = closestObject.label.replace('Press E', actionText);
    promptElement.style.display = 'block';
    
    // Show action button from MobileUI if on mobile device
    if (isMobile) {
      const actionButton = document.getElementById('action-button');
      if (actionButton) {
        actionButton.style.display = 'block';
      }
    }
  } else {
    promptElement.style.display = 'none';
    
    // Hide action button when no interactive objects are nearby
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      const actionButton = document.getElementById('action-button');
      if (actionButton) {
        actionButton.style.display = 'none';
      }
    }
  }
}

// Function to handle interaction
function handleInteraction() {
  console.log("handleInteraction called, prompt element display:", promptElement.style.display);
  
  const playerPosition = character?.position;
  if (!playerPosition) {
    console.error("No player position available");
    return;
  }
  console.log("Player position:", playerPosition);

  let closestObject = null;
  let closestDistance = Infinity;

  console.log("Checking", interactiveObjects.length, "interactive objects");
  for (const obj of interactiveObjects) {
    const worldPos = new THREE.Vector3();
    obj.mesh.getWorldPosition(worldPos);
    const distance = playerPosition.distanceTo(worldPos);
    console.log(`Object ${obj.url || obj.mesh.name}: distance=${distance.toFixed(2)}, threshold=${obj.interactionDistance}`);

    if (distance < obj.interactionDistance && distance < closestDistance) {
      closestObject = obj;
      closestDistance = distance;
    }
  }

  console.log("Closest object:", closestObject);
  
  // Special check for dance machine with direct position calculation
  if (!closestObject) {
    const danceMachinePos = new THREE.Vector3(-18, 1.5, -12); // Position of the interaction box
    const distanceToDanceMachine = playerPosition.distanceTo(danceMachinePos);
    console.log(`Direct distance to dance machine: ${distanceToDanceMachine.toFixed(2)} units`);
    
    if (distanceToDanceMachine < 1) { // Extremely small detection radius
      const danceMachineObj = interactiveObjects.find(obj => 
        obj.url === "#dance" || obj.mesh.name === 'danceMachineInteraction'
      );
      
      if (danceMachineObj) {
        console.log("Found dance machine within range, using it for interaction");
        closestObject = danceMachineObj;
      }
    }
  }

  // Only proceed if we found an interactive object
  if (closestObject) {
    // Special handling for dance machine
    if (closestObject.url === "#dance") {
      console.log("Dance machine interaction triggered!");
      
      // Get the character controller
      if (window.character?.controller) {
        console.log("Character controller found for dance machine");
        
        // Check if already dancing to toggle dancing on/off
        let isDancing = false;
        
        try {
          isDancing = typeof window.character.controller.getIsDancing === 'function' 
            ? window.character.controller.getIsDancing() 
            : false;
        } catch (e) {
          console.log("Error checking dance state:", e);
        }
        
        if (isDancing) {
          console.log("Already dancing, stopping dance animation");
          try {
            window.character.controller.stopDancing();
          } catch (e) {
            console.error("Error stopping dance:", e);
          }
          
          // Remove dance message if it exists
          const danceMessage = document.getElementById('dance-message');
          if (danceMessage && danceMessage.parentNode) {
            danceMessage.parentNode.removeChild(danceMessage);
          }
          
          // Update prompt
          promptElement.textContent = "Press E to Dance!";
          promptElement.style.display = 'block';
        } else {
          console.log("Starting dance animation");
          
          // Update prompt
          promptElement.textContent = "Press E to Stop Dancing";
          promptElement.style.display = 'block';
          
          // Start dancing
          try {
            window.character.controller.startDancing();
            console.log("Dance animation started successfully");
          } catch (e) {
            console.error("Error starting dance:", e);
          }
        }
      } else {
        console.error("Character controller not found for dance machine", window.character);
      }
      return;
    } else if (closestObject.url === "#vibeverse-portal") {
      // Handle the Vibeverse Portal - direct page redirect
      console.log("Vibeverse portal interaction triggered!");
      
      // Get player information
      const playerName = window.playerName || 'visitor';
      const playerColor = 'red'; // Default color
      const playerSpeed = '5'; // Default speed
      const referrer = window.location.href;
      
      // Build the URL with query parameters
      const portalUrl = `http://portal.pieter.com/?username=${encodeURIComponent(playerName)}&color=${encodeURIComponent(playerColor)}&speed=${encodeURIComponent(playerSpeed)}&ref=${encodeURIComponent(referrer)}`;
      
      console.log('Redirecting to Vibeverse Portal:', portalUrl);
      
      // Redirect the entire page to the portal
      window.location.href = portalUrl;
      return;
    } else if (closestObject.url === "#return-portal") {
      // Handle the Return Portal - direct page redirect
      console.log("Return portal interaction triggered!");
      
      if (window.portalInfo?.referrer) {
        // Get the referrer URL
        let referrer = window.portalInfo.referrer;
        
        try {
          // Ensure the URL has a protocol - if not, it might be a relative URL
          if (!referrer.startsWith('http://') && !referrer.startsWith('https://')) {
            // Check if it's a protocol-relative URL (starts with //)
            if (referrer.startsWith('//')) {
              referrer = 'https:' + referrer;
            } else {
              // If it's just a domain without protocol, add https://
              if (!referrer.includes('/')) {
                referrer = 'https://' + referrer;
              } else if (!referrer.includes('://')) {
                // Assume it's a full URL lacking protocol
                referrer = 'https://' + referrer;
              }
            }
          }
          
          console.log('Processed referrer URL:', referrer);
          
          // Create the complete URL with parameters
          let returnUrl;
          try {
            returnUrl = new URL(referrer);
          } catch (e) {
            console.error('Could not parse URL directly, trying with base:', e);
            // Try with current origin as base
            returnUrl = new URL(referrer, window.location.origin);
          }
          
          // Preserve the protocol, domain, and port, but make sure to add required parameters
          returnUrl.searchParams.set('portal', 'true');
          returnUrl.searchParams.set('username', window.playerName || 'visitor');
          returnUrl.searchParams.set('color', window.portalInfo.color || 'red');
          returnUrl.searchParams.set('speed', String(window.portalInfo.speed || 5));
          returnUrl.searchParams.set('ref', window.location.href);
          
          const finalUrl = returnUrl.toString();
          console.log('Redirecting to return portal:', finalUrl);
          
          // For absolute safety, make sure we're not redirecting to a relative URL
          // This ensures we go to the actual external site
          if (finalUrl.includes('://')) {
            window.location.href = finalUrl;
          } else {
            console.warn('Refusing to redirect to potentially relative URL:', finalUrl);
            // Try to fix it again with explicit https://
            window.location.href = 'https://' + finalUrl.replace(/^\/+/, '');
          }
        } catch (error) {
          console.error('Error creating return URL:', error);
          // Fallback - try to ensure it's at least pointing to the right domain
          if (referrer.includes('.')) {
            // It has a domain, so try to make it absolute
            if (!referrer.includes('://')) {
              referrer = 'https://' + referrer.replace(/^\/+/, '');
            }
            window.location.href = referrer;
          }
        }
      }
      return;
    } else if (promptElement.style.display === 'block') {
      // For other objects, only proceed if prompt is visible
      // Check if an overlay is already open before creating a new one
      const existingOverlay = document.querySelector('.arcade-overlay');
      if (existingOverlay) {
        console.log('Overlay already exists, not creating a new one');
        return;
      }

      // Special handling for vending machine
      if (closestObject.url === '#vending-machine') {
        console.log('Opening vending machine UI');
        VendingMachineUI.getInstance().show();
        return;
      }
      
      // Check if device is mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // On mobile devices, open in a new tab
        console.log('Mobile device detected, opening URL in new tab:', closestObject.url);
        window.open(closestObject.url, '_blank');
      } else {
        // On desktop, use iframe overlay as before
        createIframeOverlay(closestObject.url, closestObject.mesh, closestObject.label);
      }
    }
  }
}

// Make handleInteraction globally available
window.handleInteraction = handleInteraction;

// Create arcade screen overlay with iframe
function createIframeOverlay(url: string, arcadeMesh: THREE.Object3D, label: string) {
  // Check if an overlay is already open
  if (document.querySelector('.arcade-overlay')) {
    console.log('Overlay already exists, not creating a new one');
    return;
  }
  
  // Ensure URL is valid
  if (!url) {
    console.error('Invalid URL provided to iframe overlay');
    return;
  }
  
  // Lock controls while in arcade mode
  if (inputController) {
    inputController.enabled = false;
  }
  
  // Release mouse pointer from game
  document.exitPointerLock();
  
  // Store animation frame state to pause rendering when iframe is active
  let isGamePaused = false;
  let lastAnimationFrameId: number | null = null;
  let lowResolutionMode = false;
  let isClosed = false; // Track if this overlay has been closed
  
  // Pause background music when opening arcade game
  musicPlayer.pause();
  
  // Lower renderer quality to reduce GPU load
  const setLowQualityRendering = () => {
    // Store original settings
    const originalPixelRatio = renderer.getPixelRatio();
    const originalAntialias = renderer.capabilities.isWebGL2;
    const originalShadows = renderer.shadowMap.enabled;
    
    // Lower quality settings
    renderer.setPixelRatio(Math.min(1.0, window.devicePixelRatio));
    renderer.shadowMap.enabled = false;
    
    // Return function to restore settings
    return () => {
      renderer.setPixelRatio(originalPixelRatio);
      renderer.shadowMap.enabled = originalShadows;
    };
  };
  
  // Function to pause game rendering
  const pauseGameRendering = () => {
    isGamePaused = true;
    if (lastAnimationFrameId !== null) {
      cancelAnimationFrame(lastAnimationFrameId);
      lastAnimationFrameId = null;
    }
    
    // Dispose unused textures and buffers to free GPU memory
    scene.traverse((object) => {
      if (!object.visible && object instanceof THREE.Mesh) {
        if (object.geometry && object.geometry.dispose) {
          object.geometry.dispose();
        }
      }
    });
  };
  
  // Function to resume game rendering
  const resumeGameRendering = () => {
    isGamePaused = false;
    if (lastAnimationFrameId === null) {
      lastAnimationFrameId = requestAnimationFrame(animate);
    }
  };
  
  // Create overlay container
  const overlay = document.createElement('div');
  overlay.className = 'arcade-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
  overlay.style.zIndex = '2000';
  overlay.style.display = 'flex';
  overlay.style.flexDirection = 'column';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.opacity = '0';
  overlay.style.transition = 'opacity 0.5s ease-in-out';
  
  // Arcade screen frame
  const arcadeFrame = document.createElement('div');
  arcadeFrame.className = 'arcade-frame';
  arcadeFrame.style.width = '95%';
  arcadeFrame.style.maxWidth = '1400px';
  arcadeFrame.style.height = '90%';
  arcadeFrame.style.background = 'linear-gradient(to bottom, #444, #222)';
  arcadeFrame.style.borderRadius = '10px';
  arcadeFrame.style.padding = '20px';
  arcadeFrame.style.boxShadow = '0 0 30px rgba(138, 43, 226, 0.7)';
  arcadeFrame.style.display = 'flex';
  arcadeFrame.style.flexDirection = 'column';
  arcadeFrame.style.transform = 'scale(0.9)';
  arcadeFrame.style.transition = 'transform 0.5s ease-in-out';
  
  // Header with title and close button
  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.justifyContent = 'space-between';
  header.style.alignItems = 'center';
  header.style.marginBottom = '10px';
  
  // Left side of header with title
  const headerLeft = document.createElement('div');
  headerLeft.style.display = 'flex';
  headerLeft.style.alignItems = 'center';
  
  // Title
  const title = document.createElement('h2');
  title.textContent = label.replace('Press E to play ', '') || 'Arcade Game';
  title.style.color = '#fff';
  title.style.margin = '0';
  title.style.fontFamily = 'Arial, sans-serif';
  headerLeft.appendChild(title);
  
  // Right side of header with buttons
  const headerRight = document.createElement('div');
  headerRight.style.display = 'flex';
  headerRight.style.alignItems = 'center';
  
  // Open in new tab button
  const openTabButton = document.createElement('button');
  openTabButton.textContent = '↗️';
  openTabButton.title = 'Open in new tab';
  openTabButton.style.background = '#444';
  openTabButton.style.color = '#fff';
  openTabButton.style.border = 'none';
  openTabButton.style.borderRadius = '50%';
  openTabButton.style.width = '36px';
  openTabButton.style.height = '36px';
  openTabButton.style.marginRight = '10px';
  openTabButton.style.fontSize = '16px';
  openTabButton.style.cursor = 'pointer';
  openTabButton.style.display = 'flex';
  openTabButton.style.justifyContent = 'center';
  openTabButton.style.alignItems = 'center';
  openTabButton.style.transition = 'background 0.3s';
  
  // Hover effect for open in new tab button
  openTabButton.onmouseover = () => {
    openTabButton.style.background = '#555';
  };
  openTabButton.onmouseout = () => {
    openTabButton.style.background = '#444';
  };
  
  // Click event to open URL in new tab
  openTabButton.onclick = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };
  
  // Close button
  const closeButton = document.createElement('button');
  closeButton.textContent = 'X';
  closeButton.style.background = '#8A2BE2';
  closeButton.style.color = '#fff';
  closeButton.style.border = 'none';
  closeButton.style.borderRadius = '50%';
  closeButton.style.width = '36px';
  closeButton.style.height = '36px';
  closeButton.style.fontSize = '18px';
  closeButton.style.cursor = 'pointer';
  closeButton.style.display = 'flex';
  closeButton.style.justifyContent = 'center';
  closeButton.style.alignItems = 'center';
  closeButton.style.transition = 'background 0.3s';
  closeButton.onmouseover = () => {
    closeButton.style.background = '#7B27CC';
  };
  closeButton.onmouseout = () => {
    closeButton.style.background = '#8A2BE2';
  };
  
  // Add buttons to header right
  headerRight.appendChild(openTabButton);
  headerRight.appendChild(closeButton);
  
  // Add left and right sections to header
  header.appendChild(headerLeft);
  header.appendChild(headerRight);
  arcadeFrame.appendChild(header);
  
  // Create iframe container
  const iframeContainer = document.createElement('div');
  iframeContainer.style.flex = '1';
  iframeContainer.style.background = '#000';
  iframeContainer.style.borderRadius = '5px';
  iframeContainer.style.overflow = 'hidden';
  iframeContainer.style.position = 'relative';
  
  // Loading indicator
  const loadingIndicator = document.createElement('div');
  loadingIndicator.style.position = 'absolute';
  loadingIndicator.style.top = '50%';
  loadingIndicator.style.left = '50%';
  loadingIndicator.style.transform = 'translate(-50%, -50%)';
  loadingIndicator.style.color = '#fff';
  loadingIndicator.style.fontSize = '24px';
  loadingIndicator.style.fontFamily = 'Arial, sans-serif';
  loadingIndicator.textContent = 'Loading...';
  iframeContainer.appendChild(loadingIndicator);
  
  // Create iframe with optimized attributes
  const iframe = document.createElement('iframe');
  iframe.src = url;
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  iframe.style.border = 'none';
  iframe.style.opacity = '0';
  iframe.style.transition = 'opacity 0.5s';
  
  // Add performance optimization attributes
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('importance', 'high');
  iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups');
  
  // Use hardware acceleration where possible
  iframe.style.transform = 'translateZ(0)';
  iframe.style.backfaceVisibility = 'hidden';
  iframe.style.perspective = '1000px';
  
  // Set lower priority to let main thread handle game more efficiently
  if ('fetchPriority' in HTMLIFrameElement.prototype) {
    (iframe as any).fetchPriority = 'low';
  }
  
  // When iframe loads, hide loading indicator
  iframe.onload = () => {
    loadingIndicator.style.display = 'none';
    iframe.style.opacity = '1';
    
    // Pause game rendering once iframe is loaded and visible
    // This significantly reduces GPU/CPU usage when viewing content
    pauseGameRendering();
    
    // Apply low-quality rendering for the paused background
    const restoreQuality = setLowQualityRendering();
    
    // Store the restore function to be called on close
    iframe.dataset.restoreQuality = 'true';

    // Start scoring when iframe is loaded
    if (window.multiplayerManager) {
      try {
        const gameHelper = ThirdPartyGameHelper.getInstance(window.multiplayerManager);
        gameHelper.handleGameIframe(iframe);
      } catch (error) {
        console.error('Error initializing ThirdPartyGameHelper:', error);
      }
    } else {
      console.error('MultiplayerManager not available, scoring will not work');
    }
  };
  
  iframeContainer.appendChild(iframe);
  arcadeFrame.appendChild(iframeContainer);
  overlay.appendChild(arcadeFrame);
  document.body.appendChild(overlay);
  
  // Animate opening
  setTimeout(() => {
    overlay.style.opacity = '1';
    arcadeFrame.style.transform = 'scale(1)';
  }, 50);
  
  // Create the zoom effect from arcade to overlay
  if (arcadeMesh && camera) {
    // Store original camera position and rotation
    const originalPosition = camera.position.clone();
    const originalRotation = camera.rotation.clone();
    
    // Simple first-person position - just move camera to character's head
    const firstPersonPosition = character ? character.position.clone() : camera.position.clone();
    firstPersonPosition.y += 1.6; // Eye level
    
    // Simple animation: third-person to first-person view
    const duration = 600; // ms
    const startTime = performance.now();
    
    const animateCameraZoom = (timestamp: number) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 2); // Ease out quad
      
      // Simple interpolation between original and first-person position
      camera.position.lerpVectors(originalPosition, firstPersonPosition, easeProgress);
      
      // Render scene
      renderer.render(scene, camera);
      
      // Continue animation if not complete
      if (elapsed < duration) {
        requestAnimationFrame(animateCameraZoom);
      }
    };
    
    // Start camera animation
    requestAnimationFrame(animateCameraZoom);
    
    // When closing, animate back with simple reverse effect
    closeButton.onclick = () => {
      // Prevent multiple closure attempts
      if (isClosed) return;
      isClosed = true;
      
      // Resume background music
      musicPlayer.play();
      
      // Resume game rendering for smooth transition animation
      if (isGamePaused) {
        // Restore quality settings if needed
        if (iframe.dataset.restoreQuality === 'true') {
          const restoreQuality = setLowQualityRendering();
          restoreQuality();
          delete iframe.dataset.restoreQuality;
        }
        resumeGameRendering();
      }
      
      // Immediately disable the iframe to prevent further interaction
      iframe.style.pointerEvents = 'none';
      
      // Reset camera with simple reverse animation
      const startTime = performance.now();
      const duration = 600; // ms
      
      const animateCameraBack = (timestamp: number) => {
        // Skip animation if the overlay has been removed already
        if (!overlay.isConnected) return;
        
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = progress * progress; // Ease in quad
        
        // Simple interpolation back to original position
        camera.position.lerpVectors(firstPersonPosition, originalPosition, easeProgress);
        camera.rotation.set(
          THREE.MathUtils.lerp(camera.rotation.x, originalRotation.x, easeProgress),
          THREE.MathUtils.lerp(camera.rotation.y, originalRotation.y, easeProgress),
          THREE.MathUtils.lerp(camera.rotation.z, originalRotation.z, easeProgress)
        );
        
        // Render scene
        renderer.render(scene, camera);
        
        // Continue animation if not complete
        if (elapsed < duration && !isGamePaused && overlay.isConnected) {
          requestAnimationFrame(animateCameraBack);
        }
      };
      
      // Start camera animation
      requestAnimationFrame(animateCameraBack);
      
      // Animate closing
      arcadeFrame.style.transform = 'scale(0.9)';
      overlay.style.opacity = '0';
      
      // Remove after animation completes
      setTimeout(() => {
        // Only attempt to remove if still in the DOM
        if (overlay.isConnected) {
          overlay.remove();
        }
        
        // Re-enable controls and pointer lock
        if (inputController) {
          inputController.enabled = true;
          
          // Wait a small delay before re-enabling pointer lock
          // This prevents immediate re-locking when user might still be clicking
          setTimeout(() => {
            // Re-lock mouse to game
            canvas.requestPointerLock();
          }, 100);
        }
      }, 500);
    };
  }
}

// Add event listener for key press
document.addEventListener('keydown', (event) => {
  // E key for general interaction
  if (event.key.toLowerCase() === 'e') {
    console.log("E key pressed - triggering handleInteraction()");
    
    // Get the character controller for direct dance check
    if (window.character?.controller) {
      // Check if character is dancing using the method directly
      let isDancing = false;
      
      try {
        isDancing = typeof window.character.controller.getIsDancing === 'function' && 
                   window.character.controller.getIsDancing();
      } catch (e) {
        console.log("Error checking dance state:", e);
      }
      
      if (isDancing) {
        // If already dancing, stop dancing regardless of location
        console.log("Already dancing, stopping dance animation");
        window.character.controller.stopDancing();
        
        // Remove dance message if it exists
        const danceMessage = document.getElementById('dance-message');
        if (danceMessage && danceMessage.parentNode) {
          danceMessage.parentNode.removeChild(danceMessage);
        }
        
        // Update prompt if it exists
        if (promptElement) {
          promptElement.textContent = "Press E to Dance!";
          promptElement.style.display = 'block';
        }
        
        return; // Skip normal interaction handling when stopping dance
      }
    }
    
    // Special check for dance machine before normal interaction
    const checkDanceMachine = () => {
      const playerPosition = character?.position;
      if (!playerPosition) return false;
      
      // Check direct distance to dance machine
      const danceMachinePos = new THREE.Vector3(-18, 1.5, -12); // Position of the interaction box
      const distanceToDanceMachine = playerPosition.distanceTo(danceMachinePos);
      console.log(`E key pressed: Direct distance to dance machine: ${distanceToDanceMachine.toFixed(2)} units`);
      
      if (distanceToDanceMachine < 1) { // Extremely small detection radius
        console.log("E key: In range of dance machine, triggering dance");
        
        // Start dancing if not already dancing and controller exists
        if (window.character?.controller) {
          const wasDancing = window.character.controller.getIsDancing?.() || false;
          
          // Check current dance state
          if (wasDancing) {
            console.log("Already dancing, not starting a new dance while stopping");
            return true; // We handled the interaction, even though we didn't start a new dance
          }
          
          // Show a dance notification message
          const danceMessage = document.createElement('div');
          danceMessage.style.position = 'absolute';
          danceMessage.style.top = '20%';
          danceMessage.style.left = '50%';
          danceMessage.style.transform = 'translateX(-50%)';
          danceMessage.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          danceMessage.style.color = '#fff';
          danceMessage.style.padding = '10px 20px';
          danceMessage.style.borderRadius = '5px';
          danceMessage.style.fontFamily = 'Arial, sans-serif';
          danceMessage.style.fontSize = '18px';
          danceMessage.style.zIndex = '1000';
          danceMessage.textContent = 'Dancing!';
          danceMessage.id = 'dance-message';
          document.body.appendChild(danceMessage);
          
          // Update prompt
          promptElement.textContent = "Press E to Stop Dancing";
          promptElement.style.display = 'block';
          
          // Start dancing
          try {
            window.character.controller.startDancing();
            console.log("Dance animation started successfully from E key press");
          } catch (e) {
            console.error("Error starting dance:", e);
          }
          
          return true;
        }
      }
      return false;
    };
    
    // Check for dance machine first, if not in range, fall back to normal interaction
    if (!checkDanceMachine()) {
      // Normal interaction handling
      handleInteraction();
    }
  }
  // D key as a direct way to stop dancing
  else if (event.key.toLowerCase() === 'd') {
    console.log("D key pressed - checking if we need to stop dancing");
    
    // Get the character controller for direct dance check
    if (window.character?.controller) {
      // Check if character is dancing
      let isDancing = false;
      
      try {
        isDancing = typeof window.character.controller.getIsDancing === 'function' && 
                   window.character.controller.getIsDancing();
      } catch (e) {
        console.log("Error checking dance state:", e);
      }
      
      if (isDancing) {
        // If dancing, stop dancing
        console.log("D key: Already dancing, stopping dance animation");
        window.character.controller.stopDancing();
        
        // Remove dance message if it exists
        const danceMessage = document.getElementById('dance-message');
        if (danceMessage && danceMessage.parentNode) {
          danceMessage.parentNode.removeChild(danceMessage);
        }
        
        // Update prompt if it exists
        if (promptElement) {
          promptElement.textContent = "Press E to Dance! (After cooldown)";
          promptElement.style.display = 'block';
          
          // Update prompt after cooldown period ends
          setTimeout(() => {
            if (promptElement && promptElement.textContent?.includes('(After cooldown)')) {
              promptElement.textContent = "Press E to Dance!";
            }
          }, 1500);
        }
        
        // Show a notification that dancing stopped
        const stopDanceMsg = document.createElement('div');
        stopDanceMsg.style.position = 'absolute';
        stopDanceMsg.style.top = '20%';
        stopDanceMsg.style.left = '50%';
        stopDanceMsg.style.transform = 'translateX(-50%)';
        stopDanceMsg.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        stopDanceMsg.style.color = '#fff';
        stopDanceMsg.style.padding = '10px 20px';
        stopDanceMsg.style.borderRadius = '5px';
        stopDanceMsg.style.fontFamily = 'Arial, sans-serif';
        stopDanceMsg.style.fontSize = '18px';
        stopDanceMsg.style.zIndex = '1000';
        stopDanceMsg.textContent = 'Dance Stopped! (Cooldown 1.5s)';
        document.body.appendChild(stopDanceMsg);
        
        // Remove the message after 2 seconds
        setTimeout(() => {
          if (stopDanceMsg.parentNode) {
            stopDanceMsg.parentNode.removeChild(stopDanceMsg);
          }
        }, 2000);
      }
    }
  }
});

// Register an interactive object
function registerInteractiveObject(mesh: THREE.Object3D, url: string, label: string): void {
  interactiveObjects.push({ mesh, url, label, interactionDistance: 3 });
}

window.registerInteractiveObject = registerInteractiveObject;

// Initialize background scene
async function initBackgroundScene() {
    // Check if this is a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Create lights
    createLights(scene);
    
    // Create cafe environment
    cafeModel = await createCafe(scene, loadingManager, collisionSystem);
    
    // Set up a simple camera animation - use a smaller radius on mobile for better performance
    const cameraRadius = isMobile ? 12 : 15;
    const cameraHeight = 5;
    const cameraSpeed = isMobile ? 0.15 : 0.2; // Slightly slower on mobile
    
    function animateCamera() {
        const time = Date.now() * 0.001;
        camera.position.x = Math.cos(time * cameraSpeed) * cameraRadius;
        camera.position.z = Math.sin(time * cameraSpeed) * cameraRadius;
        camera.position.y = cameraHeight;
        camera.lookAt(new THREE.Vector3(0, 0, 0));
        renderer.render(scene, camera);
        if (startScreen.style.display !== 'none') {
            requestAnimationFrame(animateCamera);
        }
    }
    
    animateCamera();
}

// Handle URL navigation
function handleUrl(url: string, player?: THREE.Group): void {
  console.log('Handling URL:', url);
  
  // Check if the device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Handle other URLs
  if (url.startsWith('./minigames/') || 
      url.startsWith('https://fly.pieter.com') || 
      url.startsWith('https://vibesail.com') || 
      url.startsWith('https://www.hotairvibe.com')) {
    // It's a game URL
    if (isMobile) {
      // On mobile devices, open in a new tab instead of iframe
      window.open(url, '_blank');
    } else {
      // On desktop, use iframe overlay as before
      createIframeOverlay(url, player || new THREE.Group(), "Play Game");
    }
  } else if (url === '#dance') {
    // Handle dance machine
    console.log('Dance machine URL triggered');
    if (window.character?.controller) {
      window.character.controller.startDancing();
    }
  } else if (url.startsWith('http')) {
    // External URL, open in a new tab
    window.open(url, '_blank');
  } else {
    console.log('Unknown URL format:', url);
  }
}

// Initialize game
async function init(playerName: string) {
  // Check if this is a mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Store player name globally
  window.playerName = playerName;
  
  // Create character and controller with player name
  const { model, controller } = await createCharacter(scene, loadingManager, playerName);
  character = model;
  characterController = controller;
  
  // Set character on window object for dance machine interaction
  window.character = {
      controller: characterController
  };
  
  console.log("Character controller initialized:", window.character.controller);
  
  // Set up camera for character
  characterController.setCamera(camera);
  characterController.setInputController(inputController);

  // Initialize multiplayer
  multiplayerManager = new MultiplayerManager(
      WS_CLIENT_URL || `ws://${window.location.hostname}:8080`,
      playerName,
      scene,
      { model: character, controller: characterController }
  );
  
  // Also expose the multiplayerManager on the window object for forced updates
  window.multiplayerManager = multiplayerManager;
  
  // Connect to multiplayer server
  if (multiplayerManager) {
      await multiplayerManager.connect().catch(error => {
          console.error('Failed to connect to multiplayer server:', error);
      });
  }

  // Initialize inventory UI
  InventoryUI.getInstance();

  // Initialize vending machine UI
  const vendingMachineUI = VendingMachineUI.getInstance();
  vendingMachineUI.setScene(scene);

  // Set initial camera position
  const initialCameraOffset = new THREE.Vector3(0, 2, 5);
  camera.position.copy(character.position).add(initialCameraOffset);
  camera.lookAt(character.position);

  // Create return portal if the user came from a portal
  if (window.portalInfo?.fromPortal && window.portalInfo.referrer && character) {
    console.log('Creating return portal to:', window.portalInfo.referrer);
    
    // Create the return portal at the player's spawn position
    setTimeout(() => {
      if (character) {
        createReturnPortal(character.position.clone());
      }
    }, 1000); // Slight delay to ensure character is properly positioned
  }

  // Update controls info based on device
  const controlsInfo = document.getElementById('controls-info');
  if (controlsInfo) {
      if (inputController.isMobile) {
          controlsInfo.textContent = 'Use joystick to move, tap E button to interact';
      }
  }

  // Start game loop
  animate();
}

// Handle start button click
startButton.onclick = () => {
    const playerName = nameInput.value.trim() || 'Player';
    startButton.textContent = 'Loading...';
    startButton.disabled = true;
    startScreen.style.display = 'none';
    init(playerName).catch(console.error);
};

// Handle Enter key in name input
nameInput.onkeypress = (e) => {
    if (e.key === 'Enter') {
        startButton.click();
    }
};

// Animation loop - optimize for performance
function animate() {
  // Schedule next frame first for better timing
  requestAnimationFrame(animate);
  
  // Get delta time between frames
  const delta = clock.getDelta();
  
  // Skip rendering if nothing has changed
  let needsUpdate = true;
  
  // Update character controller if it exists
  if (characterController) {
    characterController.update(delta, inputController, collisionSystem, camera);
    
    // Check for interactive objects near the character
    if (character) {
      checkInteractiveObjects(character.position);
    }
  } else {
    needsUpdate = false;
  }
  
  // Update multiplayer
  if (multiplayerManager) {
    multiplayerManager.update(delta);
    
    // Debug multiplayer state every 5 seconds
    if (Math.floor(clock.getElapsedTime()) % 5 === 0 && Math.floor(clock.getElapsedTime()) !== lastDebugTime) {
      lastDebugTime = Math.floor(clock.getElapsedTime());
    }
  }
  
  // Update TWEEN animations
  TWEEN.update();
  
  // Only render if updates were made or always for multiplayer
  if (needsUpdate || multiplayerManager) {
    renderer.render(scene, camera);
  }
  
  // Update FPS counter
  fpsCounter.update();
}

// Handle window resize
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (multiplayerManager) {
    multiplayerManager.disconnect();
  }
});

// Clock for timing
const clock = new THREE.Clock();

// Initialize the background scene
initBackgroundScene().catch(console.error);

// Check if user is coming from a portal
function checkPortalParameters() {
  const urlParams = new URLSearchParams(window.location.search);
  const comingFromPortal = urlParams.get('portal') === 'true';
  
  if (comingFromPortal) {
    console.log('User is coming from a portal');
    
    // Get the referrer game
    const referrer = urlParams.get('ref');
    
    // Extract player parameters
    const username = urlParams.get('username') || 'visitor';
    const color = urlParams.get('color') || 'red';
    const speed = parseFloat(urlParams.get('speed') || '5');
    
    console.log('Portal user parameters:', { username, color, speed, referrer });
    
    // Store portal information globally
    window.portalInfo = {
      fromPortal: true,
      referrer,
      username,
      color,
      speed
    };
    
    // Skip start screen and directly join with portal parameters
    startScreen.style.display = 'none';
    init(username).catch(console.error);
  }
}

// Call this at the beginning of your app initialization
document.addEventListener('DOMContentLoaded', () => {
  checkPortalParameters();
  // ... rest of your DOMContentLoaded code ...
});

// Create a return portal to the referring game
function createReturnPortal(position: THREE.Vector3) {
  if (!window.portalInfo?.referrer) {
    console.error('Cannot create return portal: no referrer URL');
    return;
  }
  
  const referrer = window.portalInfo.referrer;
  console.log('Creating return portal at position:', position, 'to:', referrer);
  
  // Create a visual portal object
  const portalGroup = new THREE.Group();
  
  // Create portal frame (torus)
  const torusGeometry = new THREE.TorusGeometry(1.2, 0.2, 16, 32);
  const torusMaterial = new THREE.MeshStandardMaterial({
    color: 0xff8800,
    emissive: 0xff8800,
    emissiveIntensity: 0.5,
    metalness: 0.7,
    roughness: 0.3
  });
  const torus = new THREE.Mesh(torusGeometry, torusMaterial);
  torus.rotation.y = Math.PI / 2;
  portalGroup.add(torus);
  
  // Create portal disc
  const discGeometry = new THREE.CircleGeometry(1.1, 32);
  const discMaterial = new THREE.MeshBasicMaterial({
    color: 0xffaa00,
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide
  });
  const disc = new THREE.Mesh(discGeometry, discMaterial);
  disc.rotation.y = Math.PI / 2;
  portalGroup.add(disc);
  
  // Add a label
  const returnLabel = document.createElement('div');
  returnLabel.textContent = 'Return Portal';
  returnLabel.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
  returnLabel.style.color = 'white';
  returnLabel.style.padding = '5px 10px';
  returnLabel.style.borderRadius = '5px';
  returnLabel.style.fontFamily = 'Arial, sans-serif';
  returnLabel.style.fontSize = '14px';
  
  const labelTexture = new THREE.CanvasTexture(createTextCanvas('Return Portal'));
  const labelMaterial = new THREE.MeshBasicMaterial({
    map: labelTexture,
    transparent: true,
    side: THREE.DoubleSide
  });
  const labelGeometry = new THREE.PlaneGeometry(1.5, 0.4);
  const labelMesh = new THREE.Mesh(labelGeometry, labelMaterial);
  labelMesh.position.set(0, 1.7, 0);
  portalGroup.add(labelMesh);
  
  // Create collision object
  const colliderGeometry = new THREE.CylinderGeometry(1.1, 1.1, 0.2, 16);
  const colliderMaterial = new THREE.MeshBasicMaterial({ visible: false });
  const collider = new THREE.Mesh(colliderGeometry, colliderMaterial);
  collider.rotation.x = Math.PI / 2;
  portalGroup.add(collider);
  
  // Position the portal
  // Move it slightly away from the spawn point
  position.y += 0.5; // Raise it a bit off the ground
  portalGroup.position.copy(position);
  
  // Add to scene
  scene.add(portalGroup);
  
  // Register portal with interaction system
  const portalUrl = '#return-portal';
  
  if (window.registerInteractiveObject) {
    window.registerInteractiveObject(collider, portalUrl, 'Press E to return');
  }
  
  if (window.interactiveObjects) {
    window.interactiveObjects.push({
      mesh: collider,
      url: portalUrl,
      label: 'Press E to return',
      interactionDistance: 2
    });
  }
  
  // Animate the portal
  function animatePortal() {
    // Rotate the portal
    torus.rotation.z += 0.01;
    
    // Pulse the disc
    const time = Date.now() * 0.001;
    const scale = 0.9 + Math.sin(time * 2) * 0.1;
    disc.scale.set(scale, scale, scale);
    
    requestAnimationFrame(animatePortal);
  }
  
  animatePortal();
}

// Helper function to create a text canvas for labels
function createTextCanvas(text: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    console.error('Could not get 2D context for text canvas');
    return canvas;
  }
  
  canvas.width = 256;
  canvas.height = 64;
  
  // Set background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Draw text
  ctx.font = '24px Arial';
  ctx.fillStyle = 'white';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  
  return canvas;
} 